import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { SCREEN_SIZES } from '@/util/constants';

/**
 * Detects whether the viewport is below the given Tailwind breakpoint.
 * Uses `matchMedia` via `useSyncExternalStore` so the read and the subscription
 * share one API — no drift between initial state and change events.
 *
 * @example
 * const isMobile = useBreakpoint('lg'); // true when viewport < 1024px
 */
export const useBreakpoint = (breakpoint: keyof typeof SCREEN_SIZES): boolean => {
  const query = `(max-width: ${SCREEN_SIZES[breakpoint] - 1}px)`;
  // Guard SSR / workers / tests without jsdom: render-time matchMedia access
  // would throw a ReferenceError. useSyncExternalStore's getServerSnapshot
  // handles the render output, but the MediaQueryList itself must be created
  // lazily on the client.
  const mql = useMemo(
    () => (typeof window === 'undefined' ? null : window.matchMedia(query)),
    [query]
  );

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!mql) {
        return () => {};
      }
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [mql]
  );

  const getSnapshot = useCallback(() => mql?.matches ?? false, [mql]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
};
